import React, { useState, useEffect, useMemo } from 'react';
import posthog from 'posthog-js';
import Header from './Header';
import Footer from './Footer';
import './TeamCheckout.css';

interface LicenseRow {
  id: number;
  email: string;
  isCustomEmail: boolean;
  key: string;
}

const TeamCheckout: React.FC = () => {
  const [quantity, setQuantity] = useState(2);
  const [companyName, setCompanyName] = useState('');
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate a unique ID for the session/keys
  const uniqueId = useMemo(() => Math.random().toString(36).substring(2, 8).toUpperCase(), []);

  const generateKey = (company: string, index: number) => {
    const cleanCompany = company.replace(/\s+/g, '').toLowerCase() || 'company';
    return `${cleanCompany}_colorcache_${uniqueId}_${index + 1}`;
  };

  useEffect(() => {
    const newLicenses: LicenseRow[] = [];
    for (let i = 0; i < quantity; i++) {
      const existing = licenses[i];
      newLicenses.push({
        id: i + 1,
        email: existing?.isCustomEmail ? existing.email : 'Same as billing address',
        isCustomEmail: existing?.isCustomEmail || false,
        key: generateKey(companyName, i)
      });
    }
    setLicenses(newLicenses);
  }, [quantity, companyName, uniqueId]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val) && val >= 1) {
      setQuantity(val);
    }
  };

  const toggleCustomEmail = (index: number) => {
    const updated = [...licenses];
    updated[index].isCustomEmail = !updated[index].isCustomEmail;
    if (!updated[index].isCustomEmail) {
      updated[index].email = 'Same as billing address';
    } else {
      updated[index].email = '';
    }
    setLicenses(updated);
  };

  const handleEmailChange = (index: number, value: string) => {
    const updated = [...licenses];
    updated[index].email = value;
    setLicenses(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    posthog.capture('team_checkout_submitted', { quantity, total_amount: quantity * 9.99 });
    console.log('Checkout pressed. Licenses:', licenses);
    alert('Checkout pressed. We will handle the payment logic later.');
  };

  return (
    <div className="team-checkout-page">
      <Header showNav={true} />
      <div className="team-checkout-container">
        <div className="form-card wide">
          <h1>Team License Management</h1>
          <p className="subtitle">Configure your team licenses before checkout.</p>
          
          <form onSubmit={handleSubmit}>
            <div className="config-grid">
              <div className="form-group">
                <label htmlFor="companyName">Company Name</label>
                <input
                  type="text"
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quantity">Number of Licenses</label>
                <input
                  type="number"
                  id="quantity"
                  min="1"
                  value={quantity}
                  onChange={handleQuantityChange}
                  required
                />
              </div>
            </div>

            <div className="license-table-container">
              <table className="license-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>License Key</th>
                    <th>Email Address</th>
                    <th>Custom?</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license, index) => (
                    <tr key={index}>
                      <td>{license.id}</td>
                      <td>
                        <code className="license-key">{license.key}</code>
                      </td>
                      <td>
                        <input
                          type={license.isCustomEmail ? "email" : "text"}
                          value={license.email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          disabled={!license.isCustomEmail}
                          placeholder="Enter user email"
                          className={!license.isCustomEmail ? 'disabled-input' : ''}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={license.isCustomEmail}
                          onChange={() => toggleCustomEmail(index)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="checkout-summary">
              <div className="summary-row">
                <span>Total Licenses</span>
                <span>{quantity}</span>
              </div>
              <div className="summary-row total">
                <span>Total Amount</span>
                <span>${(quantity * 9.99).toFixed(2)}</span>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-large checkout-btn"
            >
              Checkout for {quantity} Licenses
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TeamCheckout;
